import { ReactNode, useEffect, useId, useState } from 'react';
import { Modal } from './Modal';
import { Button } from './Button';
import { Label, inputCls } from './form';

type Props = {
  open: boolean;
  onClose: () => void;
  /** Called with the trimmed field value on submit; the caller owns closing the dialog. */
  onSubmit: (value: string) => void;
  title?: ReactNode;
  /** Field label, shown above the input (also used as its accessible name). */
  label?: string;
  placeholder?: string;
  /** Prefilled value applied each time the dialog opens (e.g. when renaming). */
  initialValue?: string;
  submitLabel?: string;
  cancelLabel?: string;
  /** While true the submit is in flight: the field and both buttons are disabled. */
  busy?: boolean;
};

/**
 * A single-line text prompt built on {@link Modal} — the non-blocking replacement for
 * `window.prompt`, so naming a view stacks into the app's own surface instead of a browser
 * dialog. Owns its field state (reset each time it opens), submits on Enter or the confirm
 * button, and hands the trimmed value back; the caller closes the dialog itself.
 */
export const PromptDialog = ({
  open,
  onClose,
  onSubmit,
  title = 'Name',
  label,
  placeholder,
  initialValue = '',
  submitLabel = 'Save',
  cancelLabel = 'Cancel',
  busy = false,
}: Props) => {
  const [value, setValue] = useState(initialValue);
  const fieldId = useId();

  // Reset to the initial value each time the dialog OPENS (not on close, so the text doesn't
  // visibly clear while the surface is still sliding out).
  useEffect(() => {
    if (open) setValue(initialValue);
  }, [open, initialValue]);

  const trimmed = value.trim();
  const canSubmit = trimmed.length > 0 && !busy;

  const submit = () => {
    if (canSubmit) onSubmit(trimmed);
  };

  const heading = <div className="text-[15px] font-semibold text-fg-strong">{title}</div>;
  const footer = (
    <div className="flex items-center justify-end gap-2">
      <Button variant="ghost" onClick={onClose} disabled={busy}>
        {cancelLabel}
      </Button>
      <Button variant="primary" onClick={submit} disabled={!canSubmit}>
        {submitLabel}
      </Button>
    </div>
  );

  return (
    <Modal open={open} onClose={onClose} title={heading} footer={footer} className="max-w-[420px]">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        {label ? <Label htmlFor={fieldId}>{label}</Label> : null}
        <input
          id={fieldId}
          autoFocus
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={placeholder}
          // A visible <Label htmlFor> supplies the accessible name; only fall back to the
          // placeholder as an aria-label when there's no visible label.
          aria-label={label ? undefined : placeholder}
          disabled={busy}
          className={inputCls}
        />
      </form>
    </Modal>
  );
};

export default PromptDialog;
